/**
 * Reminders API Service
 * Handles all reminder operations through backend API
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const remindersApi = {
  /**
   * Get all reminders for the current user
   */
  async getUserReminders() {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found',
          reminders: []
        };
      }
      const response = await fetch(`${API_BASE}/user/reminders`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to get reminders');
      }

      return {
        success: true,
        reminders: data.data || []
      };
    } catch (error) {
      console.error('Error getting reminders:', error);
      return {
        success: false,
        error: error.message,
        reminders: []
      };
    }
  },

  /**
   * Get reminders summary for dashboard
   */
  async getRemindersSummary() {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found',
          summary: { overdue: 0, upcoming: 0, unviewed: 0, total: 0 }
        };
      }
      const response = await fetch(`${API_BASE}/user/reminders/summary`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to get reminders summary');
      }

      return {
        success: true,
        summary: data.data
      };
    } catch (error) {
      console.error('Error getting reminders summary:', error);
      return {
        success: false,
        error: error.message,
        summary: { overdue: 0, upcoming: 0, unviewed: 0, total: 0 }
      };
    }
  },

  /**
   * Create reminders for a release
   */
  async createRemindersForRelease(releaseId, releaseName, releaseDate) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/create`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          releaseId,
          releaseName,
          releaseDate
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create reminders');
      }

      return {
        success: true,
        message: data.message,
        reminders: data.data || []
      };
    } catch (error) {
      console.error('Error creating reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Create a custom/quick reminder
   */
  async createCustomReminder(reminderData) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/custom`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reminderData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create custom reminder');
      }

      return {
        success: true,
        message: data.message,
        reminder: data.data
      };
    } catch (error) {
      console.error('Error creating custom reminder:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Update reminder status (complete, snooze, etc.)
   */
  async updateReminderStatus(reminderId, status, snoozeUntil = null) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/${reminderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status,
          snoozeUntil
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update reminder');
      }

      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      console.error('Error updating reminder status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Mark reminder as viewed
   */
  async markReminderViewed(reminderId) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/${reminderId}/viewed`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to mark reminder as viewed');
      }

      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      console.error('Error marking reminder as viewed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Delete reminder
   */
  async deleteReminder(reminderId) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/${reminderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete reminder');
      }

      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      console.error('Error deleting reminder:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Sync reminders from catalog
   */
  async syncRemindersFromCatalog() {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/sync`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to sync reminders');
      }

      return {
        success: true,
        message: data.message,
        data: data.data
      };
    } catch (error) {
      console.error('Error syncing reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Create reminders for a booking
   */
  async createRemindersForBooking(bookingId, bookingData, metadata = {}) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/booking/create`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingId,
          bookingData,
          metadata
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create booking reminders');
      }

      return {
        success: true,
        message: data.message,
        reminders: data.data || []
      };
    } catch (error) {
      console.error('Error creating booking reminders:', error);
      return {
        success: false,
        error: error.message,
        reminders: []
      };
    }
  },

  /**
   * Update reminders for a booking
   */
  async updateRemindersForBooking(bookingId, updatedBooking) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/booking/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bookingData: updatedBooking
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update booking reminders');
      }

      return {
        success: true,
        message: data.message,
        updated: data.data?.updated || 0
      };
    } catch (error) {
      console.error('Error updating booking reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Delete reminders for a booking
   */
  async deleteRemindersForBooking(bookingId) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/booking/${bookingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete booking reminders');
      }

      return {
        success: true,
        message: data.message,
        deleted: data.data?.deleted || 0
      };
    } catch (error) {
      console.error('Error deleting booking reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Bulk save reminders (for syncing local to backend)
   */
  async bulkSaveReminders(reminders) {
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await fetch(`${API_BASE}/user/reminders/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reminders })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to bulk save reminders');
      }

      return {
        success: true,
        message: data.message,
        saved: data.data?.saved || 0
      };
    } catch (error) {
      console.error('Error bulk saving reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

/**
 * Helper functions for processing reminders data
 */

export const processRemindersForDisplay = (reminders) => {
  if (!Array.isArray(reminders)) return [];

  return reminders.map(reminder => ({
    ...reminder,
    isOverdue: new Date(reminder.dueDate) < new Date() && reminder.status === 'pending',
    isUpcoming: new Date(reminder.dueDate) >= new Date() && reminder.status === 'pending',
    formattedDueDate: new Date(reminder.dueDate).toLocaleDateString(),
    daysUntilDue: Math.ceil((new Date(reminder.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
  }));
};

export const getActiveReminders = (reminders) => {
  return reminders.filter(reminder => reminder.status === 'pending');
};

export const getOverdueReminders = (reminders) => {
  const now = new Date();
  return reminders.filter(reminder => 
    reminder.status === 'pending' && new Date(reminder.dueDate) < now
  );
};

export const getUpcomingReminders = (reminders) => {
  const now = new Date();
  return reminders.filter(reminder => 
    reminder.status === 'pending' && new Date(reminder.dueDate) >= now
  );
};

export const getUnviewedRemindersCount = (reminders) => {
  return reminders.filter(reminder => 
    !reminder.viewed && reminder.status === 'pending'
  ).length;
};

export const getCompletedReminders = (reminders) => {
  return reminders.filter(reminder => reminder.status === 'completed');
};

// Export for backward compatibility
export default remindersApi; 